/**
 * Who may edit a company, mirrored from the backend gate in
 * `backend/core/views/company_write_guard.py`.
 *
 * The backend is the authority — these only hide controls that would 403, so
 * a user is never offered an edit the server will refuse.
 *
 * - A company's profile (name, address, contact details, logo) needs an admin
 *   at that company: `role.is_admin_role`, surfaced as `is_company_admin`, or
 *   Django staff.
 * - `company_type`, `group` and `is_active` are platform-only: a Helix user by
 *   platform / company type, never by `is_staff` (CR-5367).
 */
import type { User } from './api/types/common';

type CompanyEditUser = Pick<User, 'is_company_admin' | 'is_staff' | 'platform' | 'company_type'> | null | undefined;

/**
 * @param user - the authenticated user, or null/undefined when not loaded yet
 * @returns true for a Helix platform user, who may change platform-only fields
 */
export function isPlatformUser(user: CompanyEditUser): boolean {
  return Boolean(user && (user.platform === 'HELIX' || user.company_type === 'helix'));
}

/**
 * @param user - the authenticated user, or null/undefined when not loaded yet
 * @returns true when the user holds an admin role at their company (or is staff)
 */
export function isCompanyAdminUser(user: CompanyEditUser): boolean {
  return Boolean(user && (user.is_company_admin || user.is_staff));
}
